import { AttsIcon } from "assets/svgComponents/PresaleBlock/ConnectWallet copy";
import BuyIcon from "assets/svgComponents/transactionsTable/BuyIcon";
import ClaimIcon from "assets/svgComponents/transactionsTable/ClaimIcon";
import clsx from "clsx";
import { format } from "date-fns";
import { useAnimation, useInView } from "framer-motion";
import { Search } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactPaginate from "react-paginate";
import { authApi } from "services/auth";
import { userStore } from "store/user";
import { TransactionOrder } from "types/User";
import { useAccount } from "wagmi";
import {
  AnimatedGradientBorder,
  GlowCard,
} from "screens/Landing/components/shared";
import { shadowAndBorderClass, gradientRowBgClass } from "../utils/classes";
import { MobileTx } from "./MobileTx";
import { useZoomScale } from "hooks/useZoomScale";

export const getTypeIcon = (type: string) => {
  if (type === "Buy")
    return <BuyIcon className="max-lg:w-[17px] max-lg:h-[17px]" />;
  if (type === "Claim")
    return <ClaimIcon className="max-lg:w-[17px] max-lg:h-[17px]" />;
  return null;
};

export default function TransactionsTable() {
  const { t } = useTranslation();

  const [searchTerm, setSearchTerm] = useState("");
  const [transactions, setTransactions] = useState<TransactionOrder[]>([]);
  const [meta, setMeta] = useState<{
    totalItems: number;
    itemCount: number;
    itemsPerPage: number;
    totalPages: number;
    currentPage: number;
  } | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 20;
  const [loading, setLoading] = useState(false);
  const { isConnected, address } = useAccount();
  const { user } = userStore();
  const [is4kDisplay, setIs4kDisplay] = useState(false);

  const sectionRef = useRef(null);
  const controlsWheel = useAnimation();
  const controlsDesktop = useAnimation();
  const isSectionInView = useInView(sectionRef, { once: true });

  useEffect(() => {
    if (isSectionInView) {
      controlsDesktop.start("visible");
      controlsWheel.start("visible");
    }
  }, [isSectionInView, controlsDesktop, controlsWheel]);

  const headerClass =
    "text-left text-[16px] max-lg:text-[12px] font-luxora-medium text-white leading-[120%]";

  useEffect(() => {
    if (isSectionInView) {
      controlsDesktop.start("visible");
      controlsWheel.start("visible");
    }
  }, [isSectionInView, controlsDesktop, controlsWheel]);

  useEffect(() => {
    if (!isConnected || !address || !user.id) {
      setTransactions([]);
      setMeta(null);
      return;
    }

    const fetchTransactions = async () => {
      setLoading(true);
      try {
        const resp = await authApi.getMyOrders({
          page: currentPage,
          limit: ITEMS_PER_PAGE,
        });
        if (resp) {
          setTransactions(resp.data || []);
          setMeta(resp.meta);
        }
      } catch (err) {
        console.error("Failed to fetch transactions:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, [currentPage, isConnected, address, user, user.id]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const updateIs4kDisplay = () => {
      setIs4kDisplay(window.innerWidth >= 2160);
    };

    updateIs4kDisplay();
    window.addEventListener("resize", updateIs4kDisplay);

    return () => {
      window.removeEventListener("resize", updateIs4kDisplay);
    };
  }, []);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const filteredTransactions = transactions.filter((transaction) => {
    const term = searchTerm.toLowerCase();
    return (
      transaction.status?.toLowerCase().includes(term) ||
      transaction.round?.name?.toLowerCase()?.includes(term) ||
      transaction.id?.toLowerCase()?.includes(term) ||
      transaction.createdAt?.toLowerCase()?.includes(term) ||
      String(transaction.amount).includes(term) ||
      String(transaction.actuallyPaidUsd).includes(term)
    );
  });

  const { scale } = useZoomScale();

  return (
    <div
      className={clsx(
        "max-lg:px-[12px] lg:mt-10 relative flex z-[20] mb-[152px] max-w-[1296px] mx-auto max-lg:max-w-[600px]",
        is4kDisplay ? "justify-around" : "justify-center",
      )}
      style={
        is4kDisplay
          ? {
              zoom: scale,
            }
          : undefined
      }
    >
      <AnimatedGradientBorder className="rounded-[16px] max-lg:rounded-[24px] w-full">
        <GlowCard className="w-full bg-[#111111] rounded-[16px] max-lg:rounded-[24px] max-lg:p-[12px] overflow-hidden animate-fade-in">
          <div
            className={clsx(
              "flex flex-col rounded-[16px] max-lg:rounded-[12px] before:!rounded-[16px] max-lg:before:!rounded-[12px] sm:flex-row sm:items-center justify-between p-[8px] max-lg:p-[8px] pl-[20px] max-lg:pl-[12px] lg:mx-[12px] lg:mt-[12px] gap-[12px] max-lg:gap-[4]",
              gradientRowBgClass,
            )}
          >
            <h1 className="text-32 max-lg:text-20m font-luxora-medium text-white animate-slide-in-left">
              {t("purchasesHistory")}
            </h1>
            <div
              className={clsx(
                "relative animate-slide-in-right h-[51px] max-lg:hidden before:!rounded-[28px] rounded-[28px]",
                shadowAndBorderClass,
              )}
            >
              <Search className="absolute left-[16px] top-1/2 transform -translate-y-1/2 z-10 w-[20px] h-[20px]" />
              <input
                type="text"
                placeholder={t("Search")}
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className={clsx(
                  "pl-[48px] pr-[12px] py-[16px] placeholder:leading-[100%] w-full sm:w-[306px] text-[16px] placeholder:text-[16px] placeholder:max-lg:text-[12px] font-luxora-regular h-[51px] bg-[#1a1a1a] rounded-[28px] focus:outline-none",
                  " placeholder:text-white/25 placeholder:font-[400] max-lg:text-[12px]",
                )}
              />
            </div>
          </div>

          <div className="max-lg:hidden px-[24px] pt-[16px] pb-[24px] overflow-x-auto">
            <div className="w-full">
              <div
                className="grid grid-cols-[1fr,1.5fr,1.5fr,1fr,0.5fr]  gap-4  py-[4px]
             px-[8px] mt-[12px] mb-[16px]"
              >
                <div className={headerClass}>{t("type")}</div>
                <div className={headerClass}>{t("date")}</div>
                <div className={headerClass}>{t("token")}</div>
                <div className={headerClass}>{t("USD")}</div>
                <div className={headerClass}>{t("price")}</div>
              </div>

              <div className="flex flex-col gap-[8px]">
                {filteredTransactions.map((transaction, index) => (
                  <div
                    key={transaction.id}
                    className={clsx(
                      "grid grid-cols-[1fr,1.5fr,1.5fr,1fr,0.5fr] rounded-[16px] bg-[#161616] items-center gap-4 p-[8px]",
                      "hover:bg-white/[0.04] transition-colors duration-200 animate-fade-in-up",
                      shadowAndBorderClass,
                    )}
                  >
                    <button
                      className="inline-flex items-center gap-[8px] h-[35px] rounded-[8px] justify-center
                          shadow-[0px_6px_7.5px_0px_rgba(0,0,0,0.06)] bg-[#252525] pl-[10px] pr-[16px] w-fit"
                    >
                      {getTypeIcon("Buy")}

                      <span className="font-luxora-regular text-[16px] leading-[120%] text-white">
                        Buy
                      </span>
                    </button>

                    <div className="text-gray-400 text-[16px] max-lg:text-[16px] font-luxora-regular">
                      {format(
                        new Date(transaction.createdAt),
                        "MMMM d, HH:mm:ss",
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <div
                        className={clsx(
                          "w-[24px] h-[24px] flex items-center rounded-full before:!rounded-full justify-center",
                          shadowAndBorderClass,
                        )}
                      >
                        <AttsIcon className="" width="22" height="22" />
                      </div>
                      <span className="font-luxora-regular text-[16px] max-lg:text-[16px] text-gray-200">
                        {transaction.amount.toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </div>

                    <div className="font-luxora-regular text-[16px] max-lg:text-[16px] text-gray-200">
                      {transaction.actuallyPaidUsd
                        ? `$${transaction.actuallyPaidUsd.toFixed(2)}`
                        : "-"}
                    </div>

                    <div className="font-luxora-regular text-[16px] max-lg:text-[16px] text-gray-200">
                      ${transaction.round.tokenPrice}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:hidden flex flex-col gap-[8px] mt-[15px]">
            {filteredTransactions.map((tx) => {
              return <MobileTx tx={tx} key={tx.id} />;
            })}
          </div>

          {filteredTransactions.length === 0 && (
            <div className="text-center py-12 text-[16px] max-lg:text-[12px] animate-fade-in">
              <div className="text-gray-500  mb-2">
                {t("transactions.notFound")}
              </div>
              <div className="text-gray-400  text-[16px] max-lg:text-[12px]">
                {t("search.tryAdjustingTerms")}
              </div>
            </div>
          )}

          {meta && meta.totalPages > 1 && (
            <div className="flex justify-center p-[24px] max-lg:p-[15px] pt-0">
              <ReactPaginate
                breakLabel="..."
                nextLabel={t("Next")}
                onPageChange={(event) => setCurrentPage(event.selected + 1)}
                pageRangeDisplayed={2}
                marginPagesDisplayed={1}
                pageCount={meta.totalPages}
                previousLabel={t("Previous")}
                renderOnZeroPageCount={null}
                initialPage={meta.currentPage - 1}
                containerClassName="flex items-center space-x-2 font-luxora-regular"
                pageLinkClassName="px-[16px] py-[8px] max-lg:px-[11px] max-lg:py-[6px] border rounded-[8px] max-lg:rounded-[11px] border-gray-600 text-white hover:bg-white/[0.08] transition-colors"
                activeLinkClassName="!bg-blue-600 !text-white !border-blue-600 shadow-md"
                previousLinkClassName="px-[16px] py-[8px] max-lg:px-[11px] max-lg:py-[6px] border rounded-[8px] max-lg:rounded-[11px] border-gray-600 text-white hover:bg-white/[0.08] transition-colors"
                nextLinkClassName="px-[16px] py-[8px] max-lg:px-[11px] max-lg:py-[6px] border rounded-[8px] max-lg:rounded-[11px] border-gray-600 text-white hover:bg-white/[0.08] transition-colors"
                disabledClassName="opacity-50 cursor-not-allowed hover:bg-transparent"
                breakClassName="px-2 py-1 text-gray-500"
              />
            </div>
          )}
        </GlowCard>
      </AnimatedGradientBorder>
    </div>
  );
}
